class SectionHeaderOffset extends HTMLElement {
  constructor() {
    super();
    
    this.header = null;
    this.section = null;
    this.headerObserver = null;
    this.includeExistingPadding = true;
  }

  connectedCallback() {
    // Get configuration from data attributes
    this.includeExistingPadding = this.dataset.includeExistingPadding !== 'false';
    
    // Find the parent section
    this.section = this.findSection();
    
    if (!this.section) {
      console.warn('Section header offset: Target section not found');
      return;
    }

    // Find header with multiple fallbacks
    this.header = this.findHeader();
    
    if (!this.header) {
      console.warn('Section header offset: Header element not found, using section padding only');
      return;
    }
    
    console.log('Section header offset: Initializing dynamic padding');
    this.initHeaderTracking();
  }

  disconnectedCallback() {
    this.cleanupHeaderTracking();
  }

  /**
   * Find target section using custom selector or default logic
   */
  findSection() {
    // Check for custom selector
    const customSelector = this.dataset.targetSelector;
    if (customSelector) {
      const section = this.closest(customSelector);
      if (section) {
        console.log(`Section header offset: Section found with custom selector "${customSelector}"`);
        return section;
      }
    }
    
    // Default: find closest section or template
    const selectors = [
      '.section',
      '[class*="template-"]',
      'section',
      '.shopify-section'
    ];
    
    for (const selector of selectors) {
      const section = this.closest(selector);
      if (section) {
        console.log(`Section header offset: Section found with selector "${selector}"`);
        return section;
      }
    }
    
    return null;
  }

  /**
   * Find header element with multiple selector fallbacks
   */
  findHeader() {
    const selectors = [
      'sticky-header',
      '.header-wrapper',
      'header.header',
      'header',
      '.shopify-section-header',
      '#shopify-section-header'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Section header offset: Header found with selector "${selector}"`);
        return element;
      }
    }
    
    return null;
  }

  /**
   * Initialize header height tracking
   * Automatically updates section padding-top based on header height
   */
  initHeaderTracking() {
    // Initial update
    this.updateSectionPadding();
    
    // Track header height changes using ResizeObserver
    if (this.header && window.ResizeObserver) {
      this.headerObserver = new ResizeObserver(() => {
        this.updateSectionPadding();
      });
      this.headerObserver.observe(this.header);
      console.log('Section header offset: ResizeObserver attached to header');
    }
    
    // Also update on scroll (for sticky header transitions)
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
    window.addEventListener('resize', this.boundHandleResize, { passive: true });
    
    console.log('Section header offset: Event listeners attached');
  }

  /**
   * Cleanup header tracking observers
   */
  cleanupHeaderTracking() {
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
    
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
    
    // Reset section padding
    if (this.section) {
      this.section.style.paddingTop = '';
    }
  }

  /**
   * Update section padding-top based on actual header height
   */
  updateSectionPadding() {
    if (!this.section || !this.header) return;
    
    const headerHeight = this.header.offsetHeight;
    
    let totalPadding = headerHeight;
    
    // Include existing section padding if configured
    if (this.includeExistingPadding) {
      const existingPaddingTop = getComputedStyle(this.section)
        .getPropertyValue('--section-padding-top')
        .trim();
      const existingPaddingValue = existingPaddingTop ? parseInt(existingPaddingTop) : 0;
      totalPadding = headerHeight + existingPaddingValue;
      
      console.log(`Section header offset: Padding updated - Header: ${headerHeight}px, Existing: ${existingPaddingValue}px, Total: ${totalPadding}px`);
    } else {
      console.log(`Section header offset: Padding updated - Header: ${headerHeight}px (existing padding ignored)`);
    }
    
    // Apply padding to section
    this.section.style.paddingTop = `${totalPadding}px`;
  }

  /**
   * Handle scroll events (for sticky header transitions)
   */
  handleScroll() {
    this.updateSectionPadding();
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    this.updateSectionPadding();
  }
}

// Register custom element
if (!customElements.get('section-header-offset')) {
  customElements.define('section-header-offset', SectionHeaderOffset);
  console.log('Section header offset: Custom element registered');
}

// Fallback initialization for non-custom-element browsers
function initSectionHeaderOffset() {
  // Check if custom elements are supported
  if (!window.customElements) {
    console.warn('Section header offset: Custom elements not supported, using fallback');
    
    // Find all section-header-offset elements
    const offsetElements = document.querySelectorAll('section-header-offset');
    
    offsetElements.forEach(element => {
      const section = element.closest('.section, [class*="template-"], section');
      
      if (!section) return;
      
      // Find header
      const headerSelectors = [
        'sticky-header',
        '.header-wrapper',
        'header.header',
        'header'
      ];
      
      let header = null;
      for (const selector of headerSelectors) {
        header = document.querySelector(selector);
        if (header) break;
      }
      
      if (!header) return;
      
      // Update padding function
      function updatePadding() {
        const headerHeight = header.offsetHeight;
        const includeExisting = element.dataset.includeExistingPadding !== 'false';
        
        let totalPadding = headerHeight;
        
        if (includeExisting) {
          const existingPadding = parseInt(
            getComputedStyle(section).getPropertyValue('--section-padding-top')
          ) || 0;
          totalPadding = headerHeight + existingPadding;
        }
        
        section.style.paddingTop = `${totalPadding}px`;
        console.log(`Section header offset (fallback): Padding updated to ${totalPadding}px`);
      }
      
      // Initial update
      updatePadding();
      
      // Track changes
      if (window.ResizeObserver) {
        const observer = new ResizeObserver(updatePadding);
        observer.observe(header);
      }
      
      window.addEventListener('scroll', updatePadding, { passive: true });
      window.addEventListener('resize', updatePadding, { passive: true });
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSectionHeaderOffset);
} else {
  initSectionHeaderOffset();
}
